import { useCallback } from 'react';
import styled from 'styled-components';
import { space } from '../../../../styles/onboardingUiTokens';
import {
  onboardingFlowContextCopy,
  signUpGateErrorCopy,
  type SignUpGateErrorReason,
} from '../../config/onboardingFlowErrorCopy';
import { Button } from '../actions/Button';
import { Notice } from '../elements/Notice';
import { Body } from '../typography/Body';
import { H1 } from '../typography/HeadingLevels';
import { useOnboardingFirebaseAuthReady } from '../../hooks/useOnboardingFirebaseAuthReady';
import { useOnboardingStore } from '../../store/onboardingStore';
import { OnboardingBody } from './OnboardingBody';
import { OnboardingFlowShell } from './OnboardingFlowShell';

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

export type OnboardingFlowErrorScreenProps = {
  reason: SignUpGateErrorReason;
};

/** Fatal cross-cutting error: flow context + specific notice, no header or footer. */
export function OnboardingFlowErrorScreen({ reason }: OnboardingFlowErrorScreenProps) {
  const errorCopy = signUpGateErrorCopy[reason];
  const resetInviteContextCache = useOnboardingStore((s) => s.resetInviteContextCache);
  const retrySignedUpPatientLoad = useOnboardingStore((s) => s.retrySignedUpPatientLoad);
  const firebaseAuthReady = useOnboardingFirebaseAuthReady();

  // Same branch as the gate: before sign-in it waits for the invite callable, after sign-in for the patient listener.
  const handleRetryLoad = useCallback(() => {
    if (firebaseAuthReady === true) {
      retrySignedUpPatientLoad();
      return;
    }
    resetInviteContextCache();
  }, [firebaseAuthReady, resetInviteContextCache, retrySignedUpPatientLoad]);

  return (
    <OnboardingFlowShell>
      <OnboardingBody>
        <ContentColumn>
          <H1>{onboardingFlowContextCopy.title}</H1>
          <Body tone="muted" variant="document">
            {onboardingFlowContextCopy.description}
          </Body>
          <Notice preset={errorCopy.noticePreset} title={errorCopy.noticeTitle}>
            {errorCopy.noticeBody}
          </Notice>
          {reason === 'patient_load_error' ? (
            <Button type="button" variant="primary" size="large" onClick={handleRetryLoad}>
              Erneut versuchen
            </Button>
          ) : null}
        </ContentColumn>
      </OnboardingBody>
    </OnboardingFlowShell>
  );
}
